import EverestVisibilityDetector from '@components/common/EverestVisibilityDetector'
import { useRef } from 'react'
import { IGoogleFont } from './briefFontSelection.types'
import { FontPreview } from './BriefFontSelectionPreview'

import { useVirtualizer } from '@tanstack/react-virtual'

interface IBriefFontSelectionList {
  filter?: string
  fonts: IGoogleFont[]
  category: string
  onSelection: (font) => void
}

const FONT_ITEM_SIZE = 125
const FONT_ITEM_MARGIN = 15

export function BriefFontSelectionList({
  filter,
  fonts,
  onSelection,
  category,
}: IBriefFontSelectionList) {
  const parentRef = useRef()

  const rowVirtualizer = useVirtualizer({
    count: fonts?.length || 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => FONT_ITEM_SIZE,
    overscan: 2,
  })

  return (
    <div
      className="flex flex-col h-[360px] overflow-auto"
      ref={parentRef}
      onScroll={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      {fonts?.length ? (
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualItem) => {
            const font = fonts?.[virtualItem.index]

            return (
              <EverestVisibilityDetector
                key={virtualItem.key}
                className="absolute top-0 left-0 w-full pb-6"
                style={{
                  transform: `translateY(${virtualItem.start}px)`,
                  height: `${virtualItem.size}px`,
                }}
              >
                {({ isVisible }) => (
                  <div className="absolute top-0 left-0 w-full pb-6">
                    <FontPreview
                      {...font}
                      isVisible={isVisible}
                      previewMode="selectable"
                      onSelection={() => onSelection(font)}
                      className="mb-6"
                      style={{
                        height: `${virtualItem.size - FONT_ITEM_MARGIN}px`,
                      }}
                    />
                  </div>
                )}
              </EverestVisibilityDetector>
            )
          })}
        </div>
      ) : (
        <div className="w-full p-10 border rounded-lg border-core-grey-30">
          {filter ? (
            <p className="w-full text-2xl text-center text-core-grey-100">
              There are no&nbsp;
              <span className="italic text-pitaya">"{filter}"</span>
              &nbsp;fonts in&nbsp;
              <span className="font-bold capitalize text-pitaya">
                {category}
              </span>
              &nbsp;category. Try searching for another one.
            </p>
          ) : (
            <p className="w-full text-2xl text-center text-core-grey-100">
              No fonts available.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
